<script lang="ts">
	import { attrs } from '#library/attrs.ts'
	import type { Blok, Vertcards } from '#library/components.schema.build.ts'
	import Render from '#components/render.svelte'
	import Shape from '#components/shape.svelte'
	import Link from '#components/link.svelte'
	import Image from '#components/image.svelte'

	interface Props {
		blok: Blok<Vertcards>
	}

	let { blok }: Props = $props()
</script>

<div class="vertcards {blok.theme}" class:split={!blok.large} {...attrs(blok)}>
	<div class="contain" class:flip={blok.flip} class:large={blok.large}>
		<div class="content" class:split={!blok.large} class:large={blok.large}>
			{#if blok.title}
				<div class="title">
					{blok.title}
				</div>
			{/if}
			{#if blok.body}
				<div class="body">
					<Render content={blok.body} />
				</div>
			{/if}
		</div>
		{#if blok.items?.length}
			<div class="cards" class:flip={blok.flip}>
				{#each blok.items as item (item._uid)}
					<div class="card {item.theme}" class:large={blok.large} class:thumbs={blok.thumbs}>
						{#if item.image?.filename}
							{#if blok.thumbs}
								<Image src={item.image.filename} alt={item.title} focus={item.image.focus} aspect="16/9" />
							{:else}
								<Shape
									src={item.image.filename}
									alt={item.title}
									name={item.shape}
									focus={item.image.focus}
								/>
							{/if}
						{/if}
						<div class="card-content">
							{#if item.title}
								<div class="card-title" class:large={blok.large}>
									{item.title}
								</div>
							{/if}
							{#if item.subtitle}
								<div class="card-subtitle">
									{item.subtitle}
								</div>
							{/if}
							{#if item.body}
								<div class="card-body">
									<Render content={item.body} />
								</div>
							{/if}
							{#if item.link?.url}
								<div class="card-link">
									<Link href={item.link.url} text={item.link.text} target={item.link.target} />
								</div>
							{/if}
						</div>
					</div>
				{/each}
			</div>
		{/if}
	</div>
</div>

<style lang="css">
	.vertcards {
		background-color: var(--c-background);
		padding-block: var(--padding-top, var(--s-104)) var(--padding-bottom, var(--s-104));

		&.split {
			padding-block: var(--padding-top, 0) 0;
			overflow: unset;

			&::before {
				@media (--from-mobile) {
					/* background-image: linear-gradient(
						to top,
						var(--c-background) 50%,
						var(--c-background) 50%,
						transparent
					); */
					content: '';
					display: block;
					height: calc(var(--padding-bottom, var(--s-140)) * 2);
					margin-block-start: calc((var(--padding-bottom, var(--s-140)) * 2) * -1);
					pointer-events: none;
					position: sticky;
					top: calc(100vh - (var(--padding-bottom, var(--s-140)) * 2) - 1px);
					width: 100%;
					z-index: 1;
				}
			}
		}
	}

	.contain {
		align-items: start;
		display: grid;
		gap: 2.5rem;
		padding-block-end: calc(var(--padding-bottom, var(--s-140)) * 2);

		@media (--from-tablet) {
			grid-template-columns: minmax(0, 7fr) minmax(0, 5fr);

			&.flip {
				grid-template-columns: minmax(0, 5fr) minmax(0, 7fr);
			}
		}

		&.large {
			padding: 0;
			@media (--from-tablet) {
				grid-template-columns: 1fr;
			}
		}
	}

	.content {
		&.large {
			text-align: center;
		}

		@media (--from-tablet) {
			&.split {
				order: 1;
				position: sticky;
				position: -webkit-sticky;
				top: 10rem;
			}
		}
	}

	.title {
		color: var(--c-foreground);
		font: 600 var(--f-h2);
		letter-spacing: var(--ls-h2);
		margin-bottom: 1rem;
		text-wrap: balance;
	}

	.body {
		font: 400 var(--f-body1);
		letter-spacing: var(--ls-body1);
	}

	.cards {
		&.flip {
			order: 1;
		}
	}

	.card {
		background-color: var(--c-background);
		color: var(--c-foreground);
		display: grid;
		gap: 2rem;
		padding: 2.5rem;

		@media (--from-mobile) {
			grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
		}

		&.large,
		&.thumbs {
			@media (--from-mobile) {
				grid-template-columns: minmax(0, 5fr) minmax(0, 7fr);
			}

			@media (--from-tablet) {
				grid-template-columns: minmax(0, 4fr) minmax(0, 8fr);

				:global(.shape) {
					margin-inline: 1.5rem;
				}
			}
		}

		& + & {
			margin-block-start: 2.5rem;
		}
	}

	.card-content {
		display: flex;
		flex-direction: column;
	}

	.card-title,
	.card-subtitle {
		font: 500 var(--f-h4);
		letter-spacing: var(--ls-h4);
	}

	.card-title {
		&.large {
			font: 500 var(--f-h3);
			letter-spacing: var(--ls-h3);
		}
	}

	.card-body {
		color: var(--c-midground);
		display: flex;
		flex-direction: column;
		font: 400 var(--f-body1);
		letter-spacing: var(--ls-body1);
		margin-block-end: auto;

		:global {
			.item {
				display: flex;
				flex-direction: column;

				&::after {
					display: none;
				}
			}

			.arrow {
				display: none;
			}

			.title {
				font: 500 var(--f-body2);
				letter-spacing: var(--ls-body2);
				padding-block: 1.25rem;
				text-decoration: underline;
				order: 1;
			}
		}
	}
</style>
